import {
    createForm,
    deleteForm,
    deleteFormField,
    downloadFormDataToExcelDirect,
    downloadFormDataToExcelWithEmail,
    getFormAnswersBySubmissionId,
    getFormById,
    getFormSubmissions,
    getListForm,
    getDeletedForms,
    permanentDeleteForm,
    restoreForm,
    updateForm,
    updateFormAnswers,
    updateFormData,
    updateFormField,
    updateFormStatus,
} from "../../api/forms";


const updateFieldQueues = {};
const lastAppliedVersions = {};
const UPDATE_DEBOUNCE_MS = 300;

function isObject(value) {
    return value && typeof value === "object" && !Array.isArray(value);
}

function mergeDeep(target, source) {
    if (!isObject(target)) {
        target = {};
    }
    if (!isObject(source)) {
        return target;
    }
    Object.keys(source).forEach(function(key) {
        const sourceValue = source[key];
        const targetValue = target[key];
        if (isObject(sourceValue)) {
            target[key] = mergeDeep(targetValue || {}, sourceValue);
        } else {
            target[key] = sourceValue;
        }
    });
    return target;
}

function scheduleUpdateField(context, siteId, formFieldId, updateFields) {
    let entry = updateFieldQueues[formFieldId];
    if (!entry) {
        entry = { pending: {}, timer: null, version: 0, siteId: siteId };
    }
    entry.siteId = siteId;
    entry.pending = mergeDeep(entry.pending, updateFields);
    if (entry.timer) {
        clearTimeout(entry.timer);
    }
    entry.version = (entry.version || 0) + 1;
    const version = entry.version;
    entry.timer = setTimeout(function() {
        const payload = entry.pending;
        delete updateFieldQueues[formFieldId];
        context.commit("UPDATE_FORM_FIELD_REQUEST");
        updateFormField(siteId, formFieldId, payload)
            .then(function(resp) {
                const data = resp && resp.data;
                const lastVersion = lastAppliedVersions[formFieldId] || 0;
                if (version < lastVersion) {
                    return;
                }
                lastAppliedVersions[formFieldId] = version;
                context.commit("UPDATE_FORM_FIELD_SUCCESS", data);
            })
            .catch(function(error) {
                context.commit("UPDATE_FORM_FIELD_FAILURE", error);
            });
    }, UPDATE_DEBOUNCE_MS);
    updateFieldQueues[formFieldId] = entry;
}

function drainQueueNow(context, formFieldId) {
    const entry = updateFieldQueues[formFieldId];
    if (!entry) return;
    if (entry.timer) {
        clearTimeout(entry.timer);
    }
    entry.version = (entry.version || 0) + 1;
    const version = entry.version;
    const siteId = entry.siteId;
    const payload = entry.pending;
    delete updateFieldQueues[formFieldId];
    context.commit("UPDATE_FORM_FIELD_REQUEST");
    updateFormField(siteId, formFieldId, payload)
        .then(function(resp) {
            const data = resp && resp.data;
            const lastVersion = lastAppliedVersions[formFieldId] || 0;
            if (version < lastVersion) {
                return;
            }
            lastAppliedVersions[formFieldId] = version;
            context.commit("UPDATE_FORM_FIELD_SUCCESS", data);
        })
        .catch(function(error) {
            context.commit("UPDATE_FORM_FIELD_FAILURE", error);
        });
}

const forms = {
    namespaced: true,
    state: {
        form: null,
        forms: [],
        deletedForms: [],
        totalCount: 0,
        submissions: [],
        totalPages: 0,
        currentPage: 1,
        pageSize: 10,
        loading: false,
        error: null,
        updateLoading: false,
        updateError: null,
        deletedFormsLoading: false,
    },
    mutations: {
        CREATE_FORM_REQUEST(state) {
            state.loading = true;
            state.error = null;
        },
        CREATE_FORM_SUCCESS(state, form) {
            state.loading = false;
            state.form = form;
        },
        CREATE_FORM_FAILURE(state, error) {
            state.loading = false;
            state.error = error;
        },
        GET_LIST_FORM_REQUEST(state) {
            state.loading = true;
            state.error = null;
        },
        GET_LIST_FORM_SUCCESS(state, forms) {
            state.loading = false;
            state.forms = forms;
        },
        GET_LIST_FORM_FAILURE(state, error) {
            state.loading = false;
            state.error = error;
        },
        GET_FORM_REQUEST(state) {
            state.loading = true;
            state.error = null;
        },
        GET_FORM_SUCCESS(state, form) {
            state.loading = false;
            state.form = form;
        },
        GET_FORM_FAILURE(state, error) {
            state.loading = false;
            state.error = error;
        },
        UPDATE_FORM_REQUEST(state) {
            state.updateLoading = true;
            state.updateError = null;
        },
        UPDATE_FORM_SUCCESS(state, updatedForm) {
            state.updateLoading = false;
            state.form = updatedForm.form;
            state.formFields = updatedForm.updatedFormFields;
        },
        UPDATE_FORM_FAILURE(state, error) {
            state.updateLoading = false;
            state.updateError = error;
        },
        UPDATE_FORM_FIELD_REQUEST(state) {
            state.updateLoading = true;
            state.updateError = null;
        },
        UPDATE_FORM_FIELD_SUCCESS(state, updatedField) {
            state.updateLoading = false;
            if (state.form && state.form.formFields) {
                var idx = -1;
                for (var i = 0; i < state.form.formFields.length; i++) {
                    if (state.form.formFields[i] && state.form.formFields[i]._id === updatedField._id) {
                        idx = i;
                        break;
                    }
                }
                if (idx !== -1) {
                    state.form.formFields.splice(idx, 1, updatedField);
                }
            }
        },
        UPDATE_FORM_FIELD_FAILURE(state, error) {
            state.updateLoading = false;
            state.updateError = error;
        },
        DELETE_FORM_REQUEST(state) {
            state.loading = true;
            state.error = null;
        },
        DELETE_FORM_SUCCESS(state, formId) {
            state.loading = false;
            state.forms = state.forms.filter((form) => form._id !== formId);
        },
        DELETE_FORM_FAILURE(state, error) {
            state.loading = false;
            state.error = error;
        },
        DELETE_FORM_FIELD_REQUEST(state) {
            state.loading = true;
            state.error = null;
        },
        DELETE_FORM_FIELD_SUCCESS(state, fieldId) {
            state.loading = false;
            if (state.form && state.form.formFields) {
                state.form.formFields = state.form.formFields.filter(function(field) {
                    return field && field._id !== fieldId;
                });
            }
        },
        DELETE_FORM_FIELD_FAILURE(state, error) {
            state.loading = false;
            state.error = error;
        },
        GET_FORM_ANSWERS_REQUEST(state) {
            state.loading = true;
            state.error = null;
        },
        GET_FORM_ANSWERS_SUCCESS(state, { submissions, totalPages, currentPage }) {
            state.loading = false;
            state.submissions = submissions;
            state.totalPages = totalPages;
            state.currentPage = currentPage;
        },
        GET_FORM_ANSWERS_FAILURE(state, error) {
            state.loading = false;
            state.error = error;
        },
        UPDATE_FORM_DATA_REQUEST(state) {
            state.updateLoading = true;
            state.updateError = null;
        },
        UPDATE_FORM_DATA_SUCCESS(state, updatedForm) {
            state.updateLoading = false;
            state.form = updatedForm;
        },
        UPDATE_FORM_DATA_FAILURE(state, error) {
            state.updateLoading = false;
            state.updateError = error;
        },
        SET_PAGE_SIZE(state, pageSize) {
            state.pageSize = pageSize;
        },
        SET_CURRENT_PAGE(state, page) {
            state.currentPage = page;
        },
        SET_FORM_TOTAL_COUNT(state, totalCount) {
            state.totalCount = totalCount;
        },
        DOWNLOAD_FORM_DATA_REQUEST(state) {
            state.loading = true;
            state.error = null;
        },
        DOWNLOAD_FORM_DATA_SUCCESS(state) {
            state.loading = false;
        },
        DOWNLOAD_FORM_DATA_FAILURE(state, error) {
            state.loading = false;
            state.error = error;
        },
        UPDATE_FORM_ANSWERS_REQUEST(state) {
            state.updateLoading = true;
            state.updateError = null;
        },
        UPDATE_FORM_ANSWERS_SUCCESS(state, updatedData) {
            state.updateLoading = false;
            state.answersData = updatedData;
        },
        UPDATE_FORM_ANSWERS_FAILURE(state, error) {
            state.updateLoading = false;
            state.updateError = error;
        },
        GET_DELETED_FORMS_REQUEST(state) {
            state.deletedFormsLoading = true;
            state.error = null;
        },
        GET_DELETED_FORMS_SUCCESS(state, deletedForms) {
            state.deletedFormsLoading = false;
            state.deletedForms = deletedForms;
        },
        GET_DELETED_FORMS_FAILURE(state, error) {
            state.deletedFormsLoading = false;
            state.error = error;
        },
        RESTORE_FORM_REQUEST(state) {
            state.loading = true;
            state.error = null;
        },
        RESTORE_FORM_SUCCESS(state, formId) {
            state.loading = false;
            state.deletedForms = state.deletedForms.filter((form) => form._id !== formId);
        },
        RESTORE_FORM_FAILURE(state, error) {
            state.loading = false;
            state.error = error;
        },
        PERMANENT_DELETE_FORM_REQUEST(state) {
            state.loading = true;
            state.error = null;
        },
        PERMANENT_DELETE_FORM_SUCCESS(state, formId) {
            state.loading = false;
            state.deletedForms = state.deletedForms.filter((form) => form._id !== formId);
        },
        PERMANENT_DELETE_FORM_FAILURE(state, error) {
            state.loading = false;
            state.error = error;
        },
    },
    actions: {
        async createForm({ commit, state }, form) {
            commit("CREATE_FORM_REQUEST");
            try {
                const { data } = await createForm(form.siteId, form.title, form.fields);
                commit("SET_FORM_TOTAL_COUNT", state.totalCount + 1);
                commit("CREATE_FORM_SUCCESS", data);
                return data;
            } catch (error) {
                commit("CREATE_FORM_FAILURE", error);
                throw error;
            }
        },
        async getListForm({ commit }, siteId) {
            commit("GET_LIST_FORM_REQUEST");
            try {
                const { data } = await getListForm();
                const { forms, totalCount } = data;
                
                // UI'da sadece aktif formları göster (çöp kutusu ve kalıcı silinen hariç)
                const activeFormsForUI = forms.filter(f => !f.isTrash && !f.isDeleted);
                
                commit("GET_LIST_FORM_SUCCESS", activeFormsForUI);
                
                // Limit kontrolü için backend'den dönen totalCount'u kullan
                // totalCount: Çöp kutusundakiler DAHİL, Kalıcı silinmişler HARİÇ
                commit("SET_FORM_TOTAL_COUNT", totalCount);
                
                return data;
            } catch (error) {
                commit("GET_LIST_FORM_FAILURE", error);
                throw error;
            }
        },
         async syncForms({ commit }) {
            commit("GET_LIST_FORM_REQUEST");
            try {
                const { data } = await getListForm();
                const { forms, totalCount } = data;
                
                // UI'da sadece aktif formları göster (çöp kutusu ve kalıcı silinen hariç)
                const activeFormsForUI = forms.filter(f => !f.isTrash && !f.isDeleted);
                
                commit("GET_LIST_FORM_SUCCESS", activeFormsForUI);
                
                // Limit kontrolü için backend'den dönen totalCount'u kullan
                commit("SET_FORM_TOTAL_COUNT", totalCount);
                
                return data;
            } catch (error) {
                commit("GET_LIST_FORM_FAILURE", error);
                throw error;
            }
        },
        async getFormById({ commit }, { siteId, formId }) {
            commit("GET_FORM_REQUEST");
            try {
                const { data } = await getFormById(formId);
                commit("GET_FORM_SUCCESS", data);
                return data;
            } catch (error) {
                commit("GET_FORM_FAILURE", error);
                throw error;
            }
        },
        async updateForm({ commit }, { siteId, formId, formData, formFieldsData }) {
            commit("UPDATE_FORM_REQUEST");
            try {
                for (const fieldData of formFieldsData) {
                    if (!fieldData.type || !fieldData.label || fieldData.order == null) {
                        throw new Error("FormField validation failed: type, label, and order are required.");
                    }
                }
                const { data } = await updateForm(siteId, formId, formData, formFieldsData);
                commit("UPDATE_FORM_SUCCESS", data);
                return data;
            } catch (error) {
                commit("UPDATE_FORM_FAILURE", error);
                throw error;
            }
        },
        async updateFormField({ commit }, { siteId, formFieldId, updateFields }) {
            scheduleUpdateField({ commit: commit }, siteId, formFieldId, updateFields);
        },
        async updateFormStatus({ commit }, { siteId, submissionId, newStatus }) {
            commit("UPDATE_FORM_REQUEST");
            try {
                const { data } = await updateFormStatus(siteId, submissionId, newStatus);
                commit("UPDATE_FORM_SUCCESS", data);
                return data;
            } catch (error) {
                commit("UPDATE_FORM_FAILURE", error);
                throw error;
            }
        },
        async deleteForm({ commit, state }, { siteId, formId }) {
            commit("DELETE_FORM_REQUEST");
            try {
                await deleteForm(siteId, formId);
                commit("DELETE_FORM_SUCCESS", formId);
                commit("SET_FORM_TOTAL_COUNT", state.totalCount - 1);
            } catch (error) {
                commit("DELETE_FORM_FAILURE", error);
                throw error;
            }
        },
        async deleteFormField({ commit }, { siteId, fieldId }) {
            commit("DELETE_FORM_FIELD_REQUEST");
            try {
                await deleteFormField(siteId, fieldId);
                commit("DELETE_FORM_FIELD_SUCCESS", fieldId);
            } catch (error) {
                commit("DELETE_FORM_FIELD_FAILURE", error);
                throw error;
            }
        },
        async getFormSubmissions({ commit, state }, { siteId, formId, status = "notRead", page = 1, perPage }) {
            commit("GET_FORM_ANSWERS_REQUEST");
            try {
                perPage = perPage || state.perPage || 10;
                const { data } = await getFormSubmissions(siteId, formId, status, page, perPage);
                commit("GET_FORM_ANSWERS_SUCCESS", {
                    submissions: data.submissions,
                    totalPages: data.totalPages,
                    currentPage: page,
                });
                return data;
            } catch (error) {
                commit("GET_FORM_ANSWERS_FAILURE", error);
                throw error;
            }
        },
        async updateFormData({ commit }, { siteId, formId, formData }) {
            commit("UPDATE_FORM_DATA_REQUEST");
            try {
                const { data } = await updateFormData(siteId, formId, formData);
                commit("UPDATE_FORM_DATA_SUCCESS", data);
                return data;
            } catch (error) {
                commit("UPDATE_FORM_DATA_FAILURE", error);
                throw error;
            }
        },
        async getFormAnswersBySubmissionId({ commit }, { siteId, submissionId }) {
            commit("GET_FORM_ANSWERS_REQUEST");
            try {
                const { data } = await getFormAnswersBySubmissionId(siteId, submissionId);
                commit("GET_FORM_ANSWERS_SUCCESS", data);
                return data;
            } catch (error) {
                commit("GET_FORM_ANSWERS_FAILURE", error);
                throw error;
            }
        },
        setPageSize({ commit }, pageSize) {
            commit("SET_PAGE_SIZE", pageSize);
        },
        setCurrentPage({ commit }, page) {
            commit("SET_CURRENT_PAGE", page);
        },
        async downloadFormDataToExcelWithEmail({ commit }, { siteId, formId, submissionId = null, email }) {
            commit("DOWNLOAD_FORM_DATA_REQUEST");
            try {
                const response = await downloadFormDataToExcelWithEmail(siteId, formId, submissionId, email);

                console.log("Email download response:", response);

                commit("DOWNLOAD_FORM_DATA_SUCCESS");

                return response.data;
            } catch (error) {
                console.error("Excel email indirme hatası:", error);
                commit("DOWNLOAD_FORM_DATA_FAILURE", error);
                throw error;
            }
        },
        async downloadFormDataToExcelDirect({ commit }, { siteId, formId, submissionId = null }) {
            commit("DOWNLOAD_FORM_DATA_REQUEST");
            try {
                const response = await downloadFormDataToExcelDirect(siteId, formId, submissionId);

                console.log("Direct download response:", response);

                const { downloadUrl } = response.data;

                commit("DOWNLOAD_FORM_DATA_SUCCESS");

                return downloadUrl;
            } catch (error) {
                console.error("Excel direkt indirme hatası:", error);
                commit("DOWNLOAD_FORM_DATA_FAILURE", error);
                throw error;
            }
        },
        async updateFormAnswers({ commit }, { siteId, submissionId, answersData }) {
            commit("UPDATE_FORM_ANSWERS_REQUEST");
            try {
                const { data } = await updateFormAnswers(siteId, submissionId, answersData);
                commit("UPDATE_FORM_ANSWERS_SUCCESS", data);
                return data;
            } catch (error) {
                commit("UPDATE_FORM_ANSWERS_FAILURE", error);
                throw error;
            }
        },
        async getDeletedForms({ commit }, siteId) {
            commit("GET_DELETED_FORMS_REQUEST");
            try {
                const { data } = await getDeletedForms(siteId);
                const { forms, totalCount } = data;
                commit("GET_DELETED_FORMS_SUCCESS", forms);
                return data;
            } catch (error) {
                commit("GET_DELETED_FORMS_FAILURE", error);
                throw error;
            }
        },
        async restoreForm({ commit, state }, { siteId, formId }) {
            commit("RESTORE_FORM_REQUEST");
            try {
                await restoreForm(siteId, formId);
                commit("RESTORE_FORM_SUCCESS", formId);
            } catch (error) {
                commit("RESTORE_FORM_FAILURE", error);
                throw error;
            }
        },
        async permanentDeleteForm({ commit, state }, { siteId, formId }) {
            commit("PERMANENT_DELETE_FORM_REQUEST");
            try {
                await permanentDeleteForm(siteId, formId);
                commit("PERMANENT_DELETE_FORM_SUCCESS", formId);
            } catch (error) {
                commit("PERMANENT_DELETE_FORM_FAILURE", error);
                throw error;
            }
        },
    },
    getters: {
        form: (state) => state.form,
        forms: (state) => state.forms,
        submissions: (state) => state.submissions,
        totalPages: (state) => state.totalPages,
        currentPage: (state) => state.currentPage,
        pageSize: (state) => state.pageSize,
        loading: (state) => state.loading,
        error: (state) => state.error,
        updateLoading: (state) => state.updateLoading,
        updateError: (state) => state.updateError,
    },
};

export default forms;
